import { useMemo } from 'react'
import { disambiguateDuplicateLabels } from '../helpers/label-duplicates'
import type { PortraitFilterOption } from '../types/portrait-filter-option'

type OptionsQuery<T> = {
  data: T[] | undefined
  isLoading: boolean
  isError: boolean
  error: unknown
  refetch: () => unknown
}

/**
 * A filter dropdown's options and load state from the query that lists them (grades, homerooms,
 * custom groups). `toOption` should be a stable function, such as a module-level one, so the list
 * is only rebuilt when the data changes. Options that share a name are numbered so their rows can
 * be told apart, and `onRetry` asks the query again after a failure.
 */
export function usePortraitFilterOptions<T>(
  query: OptionsQuery<T>,
  toOption: (item: T) => PortraitFilterOption
): {
  options: PortraitFilterOption[]
  isLoading: boolean
  isError: boolean
  errorMessage: string | undefined
  onRetry: () => void
} {
  const { data, isLoading, isError, error, refetch } = query
  const options = useMemo(
    () => disambiguateDuplicateLabels((data ?? []).map(toOption)),
    [data, toOption]
  )

  return {
    options,
    isLoading,
    isError,
    errorMessage: error instanceof Error ? error.message : undefined,
    onRetry: () => {
      void refetch()
    },
  }
}
